import { useTheme } from 'app/shopify/theme';
import { ActionPressable, type ActionProps } from '../shared/ActionPressable';
import { ActionIconSlot, ActionSpinner, type ActionIcon } from '../shared/ActionContent';
import { buttonPresentation, iconButtonSize, type ButtonVariant } from '../shared/button-presentation';
import { ArrowRightIcon } from './ArrowRightIcon';

export type IconButtonVariant = 'primary' | 'secondary' | 'secondaryNeutral' | 'tertiary' | 'ghost' | 'ghostNeutral';
export type IconButtonSize = 'xSmall' | 'small' | 'medium' | 'large';
export interface IconButtonProps extends ActionProps {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  inverted?: boolean;
  icon?: ActionIcon;
}
const variants: Record<IconButtonVariant, ButtonVariant> = {
  primary: 'primary',
  secondary: 'secondaryTonal',
  secondaryNeutral: 'secondaryNeutral',
  tertiary: 'tertiary',
  ghost: 'ghost',
  ghostNeutral: 'textSecondary',
};
const iconSizes = {
  xSmall: 'sizing.icon.s',
  small: 'sizing.icon.sm',
  medium: 'sizing.icon.default',
  large: 'sizing.icon.default',
} as const;

export function IconButton({ variant = 'primary', size = 'large', inverted = false, icon, ...props }: IconButtonProps) {
  const theme = useTheme();
  const dimension = Number(theme.tokens.sizing[iconButtonSize[size]]);
  const iconSize = Number(theme.tokens.sizing[iconSizes[size]]);
  return (
    <ActionPressable
      {...props}
      width={dimension}
      height={dimension}
      appearance={(interaction) => buttonPresentation(theme, variants[variant], inverted, interaction, true)}
    >
      {(interaction) => {
        const visual = buttonPresentation(theme, variants[variant], inverted, interaction, true);
        return interaction.loading ? (
          <ActionSpinner
            color={visual.spinnerColor}
            size={size === 'xSmall' ? 'xSmall' : size === 'large' ? 'large' : 'small'}
          />
        ) : (
          <ActionIconSlot icon={icon ?? ArrowRightIcon} size={iconSize} color={visual.foregroundColor} />
        );
      }}
    </ActionPressable>
  );
}
